import type { SelfVoiceData, SelfSavePayload, AudioFailure, SelfStamp } from '@shared/types';
import { xhrUpload, type UploadProgress } from '@/lib/xhrUpload';

// Eigener, schlanker Client für die öffentliche Self-Service-Fläche (/api/self/*).
// BEWUSST NICHT der Admin-`request`-Wrapper: die Self-Seite liegt AUSSERHALB von
// Zero Trust, es gibt also keine 302-ZT-Redirect-Logik. Auth läuft rein über das
// HttpOnly-Session-Cookie, daher `credentials: 'include'` bei jedem Request.

const API_BASE = '/api';

// Wirft einen Error, dessen `message` der Fehlercode aus dem Body (`error`) ist —
// so kann die UI z.B. `invalid` / `too_many_attempts` gezielt behandeln.
async function throwFromResponse(res: Response): Promise<never> {
  const text = await res.text();
  let message = text;
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(text);
    if ((parsed as { error?: unknown })?.error) message = String((parsed as { error?: unknown }).error);
  } catch {
    // kein JSON-Body — Rohtext als Message belassen, body bleibt null.
  }
  const err = new Error(message || `${res.status}`);
  (err as { status?: number }).status = res.status;
  (err as { body?: unknown }).body = parsed;
  throw err;
}

async function selfJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) return throwFromResponse(res);
  return res.json() as Promise<T>;
}

// (Multipart-Uploads laufen jetzt über xhrUpload() für echten Byte-Progress.)

export interface SelfAudioMeta {
  category: string | null;
  language: string;
  dialect: string | null;
  accent: string | null;
  is_demomix: boolean;
  freetext_suffix: string | null;
}

export const selfApi = {
  // Login → Set-Cookie (Session). Wirft bei !ok mit dem Body-Fehlercode.
  login: (token: string, password: string): Promise<void> =>
    selfJson<{ ok: true }>(`/self/${token}/login`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }).then(() => undefined),

  getData: (token: string): Promise<SelfVoiceData> =>
    selfJson<SelfVoiceData>(`/self/${token}`),

  // Aktualitaets-Ping (Spec 2026-09-15 §5/§6). Die Antwort traegt NUR den
  // Fingerabdruck `version` — weder wer noch wann bei TRO gespeichert wurde.
  getStamp: (token: string): Promise<SelfStamp> =>
    selfJson<SelfStamp>(`/self/${token}/stamp`),

  save: (token: string, payload: SelfSavePayload): Promise<{ ok: true; audio_failures: AudioFailure[] }> =>
    selfJson<{ ok: true; audio_failures: AudioFailure[] }>(`/self/${token}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  uploadAudio: (token: string, body: Blob, meta: SelfAudioMeta, originalName: string, onProgress?: (p: UploadProgress) => void): Promise<void> => {
    // `body` = client-seitig normalisiertes MP3 192k (audioConvert.ts); Content-Type
    // audio/mpeg. `originalName` (Roh-Name) nur als original_filename, Storage-Name via Naming.
    const p = new URLSearchParams({ language: meta.language, is_demomix: String(meta.is_demomix), filename: originalName });
    if (meta.category) p.set('category', meta.category);
    if (meta.dialect) p.set('dialect', meta.dialect);
    if (meta.accent) p.set('accent', meta.accent);
    if (meta.freetext_suffix) p.set('freetext_suffix', meta.freetext_suffix);
    return xhrUpload<{ audio: unknown }>(`${API_BASE}/self/${token}/audios?${p.toString()}`, body, onProgress, 'audio/mpeg').then(() => undefined);
  },

  audioUrl: (token: string, filename: string): string =>
    `${API_BASE}/self/${token}/audio/${encodeURIComponent(filename)}`,
};
